// Generic full-screen modal shell (Escape-to-close, backdrop click, scroll-lock) used by
// EinflussfaktorenDetailsModal and other analysis-area modals — no ML/SHAP-specific logic.
import { ReactNode, useEffect } from "react"
import { createPortal } from "react-dom"

type AnalysisModalProps = {
    title: string
    onClose: () => void
    children: ReactNode
}

export default function AnalysisModal({
    title,
    onClose,
    children
}: AnalysisModalProps) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
    }, [onClose])

    useEffect(() => {
        const prev = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = prev
        }
    }, [])

    return createPortal(
        <div
            className={
                "fixed inset-0 z-[140] flex items-center justify-center p-4"
            }
        >
            <div
                className={"absolute inset-0 bg-black/45 backdrop-blur-sm"}
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-modal="true"
                className={
                    "relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-tremor-border bg-white shadow-2xl"
                }
            >
                <div
                    className={
                        "flex flex-shrink-0 items-start justify-between gap-4 border-b border-tremor-border bg-tremor-background-muted px-5 py-4"
                    }
                >
                    <h4
                        className={
                            "text-base font-semibold text-tremor-content-emphasis"
                        }
                    >
                        {title}
                    </h4>
                    <button
                        type="button"
                        onClick={onClose}
                        className={
                            "rounded-md border border-tremor-border bg-white px-3 py-1.5 text-sm font-medium text-tremor-content-emphasis hover:bg-tremor-background-muted"
                        }
                    >
                        Schließen
                    </button>
                </div>
                <div className={"overflow-y-auto"}>{children}</div>
            </div>
        </div>,
        document.body
    )
}
